import { SexoEnum } from 'graphql/types.generated'

import alturaMaior5anos from './parametros/altura5a19anos.json'
import alturaAte5anos from './parametros/alturaAte5anos.json'
import alturaPreTermo from './parametros/alturaPreTermo.json'
import imcMaior5anos from './parametros/imc5a19anos.json'
import imcAte5anos from './parametros/imcAte5anos.json'
import perimetroCefalico from './parametros/perimetroCefalico.json'
import perimetroCefalicoPreTermo from './parametros/perimetroCefalicoPreTermo.json'
import pesoMaior5anos from './parametros/peso5a10anos.json'
import pesoAte5anos from './parametros/pesoAte5anos.json'
import pesoPreTermo from './parametros/pesoPreTermo.json'
/**
 * Fração máxima de anos antes de completar 61 meses (mínimo das referências de mais de 5 anos).
 * Isto é necessário pois valores entre 5 e 5.08 são maiores ou iguais a 5, mas são menores que 61
 * meses (quando multiplicados por 12).
 */
const THRESHOLD_5_ANOS = 5.08

export type TipoMedicao = 'imc' | 'peso' | 'perimetroCefalico' | 'altura'

export enum Percentil {
  /* sd é o equivalente em inglês para: desvio padrão */
  sd3pos = 3,
  sd2pos = 2,
  sd1pos = 1,
  sd2neg = -2,
  sd3neg = -3,
}

interface Parametros {
  sex: 1 | /*Masculino*/ 2 /*Feminino*/
  age: number
  l: number
  m: number
  s: number
}

type MedicaoMap = Record<TipoMedicao, Parametros[]>

export const createParameters = (
  sexo: SexoEnum.FEMININO | SexoEnum.MASCULINO,
  idadeEmAnos: number,
  medicao: TipoMedicao,
  roundFunction = Math.floor,
  shouldRenderPreTermo: boolean = false
) => {
  //Para evitar que imprecisões de ponto flutuante afetem quais parâmetros serão usados
  // No pré-termo, o arredondamento pode aumentar a idade em até 2 dias indevidamente.
  const idadeRounded = shouldRenderPreTermo ? idadeEmAnos : round(idadeEmAnos, 2)
  const json = getJson(idadeRounded, medicao, shouldRenderPreTermo)
  /**
   * A idade para crianças menores de 5 anos deve estar em dias (1 mês = 30,4375 dias, assim, 12 meses = 365,25).
   * Para maiores de 5 anos e menores de 19 anos deve estar em meses.
   */
  const convertIdade = roundFunction(idadeRounded <= THRESHOLD_5_ANOS ? idadeRounded * 365.25 : idadeRounded * 12)
  const convertSexo = sexo === SexoEnum.FEMININO ? 2 : 1
  const data = json.find((d) => {
    return d.sex === convertSexo && d.age === convertIdade
  })

  const calcularZscoreFinal = (valorMedicao: number) => {
    const Zindex = (Math.pow(valorMedicao / data.m, data.l) - 1) / (data.s * data.l)
    let ZscoreFinal = 0

    if (Zindex > 3) {
      ZscoreFinal = 3 + (valorMedicao - getSd3pos()) / getSd23pos()
    } else if (Zindex < -3) {
      ZscoreFinal = -3 + (valorMedicao - getSd3neg()) / getSd23neg()
    } else if (Math.abs(Zindex) <= 3) {
      ZscoreFinal = Zindex
    }
    return ZscoreFinal
  }

  const calcularPercentil = (percentil: number) => {
    if (data) {
      return round(data.m * Math.pow(1 + data.l * data.s * percentil, 1 / data.l))
    }
  }

  const getSd3pos = () => {
    return calcularPercentil(Percentil.sd3pos)
  }

  const getSd2pos = () => {
    return calcularPercentil(Percentil.sd2pos)
  }

  const getSd1pos = () => {
    return calcularPercentil(Percentil.sd1pos)
  }

  const getSd2neg = () => {
    return calcularPercentil(Percentil.sd2neg)
  }

  const getSd3neg = () => {
    return calcularPercentil(Percentil.sd3neg)
  }

  const getSd23pos = () => {
    return getSd3pos() - getSd2pos()
  }

  const getSd23neg = () => {
    return getSd2neg() - getSd3neg()
  }

  return {
    calcularZscoreFinal,
    calcularPercentil,
    getSd3pos,
    getSd2pos,
    getSd1pos,
    getSd2neg,
    getSd3neg,
    getSd23pos,
    getSd23neg,
  }
}

const preTermoMap = {
  peso: pesoPreTermo,
  altura: alturaPreTermo,
  perimetroCefalico: perimetroCefalicoPreTermo,
} as MedicaoMap

const ate5anosMap = {
  imc: imcAte5anos,
  peso: pesoAte5anos,
  altura: alturaAte5anos,
  perimetroCefalico: perimetroCefalico,
} as MedicaoMap

const maior5anosMap = {
  imc: imcMaior5anos,
  peso: pesoMaior5anos,
  altura: alturaMaior5anos,
  perimetroCefalico: perimetroCefalico,
} as MedicaoMap

const mapJsonPorFaixaEtaria: Record<'preTermo' | 'ate5anos' | 'maior5anos', MedicaoMap> = {
  preTermo: preTermoMap,
  ate5anos: ate5anosMap,
  maior5anos: maior5anosMap,
}

function getJson(idade: number, medicao: TipoMedicao, shouldRenderPreTermo: boolean): Parametros[] {
  let faixa: keyof typeof mapJsonPorFaixaEtaria

  if (shouldRenderPreTermo) {
    faixa = 'preTermo'
  } else if (idade <= THRESHOLD_5_ANOS) {
    faixa = 'ate5anos'
  } else {
    faixa = 'maior5anos'
  }

  return mapJsonPorFaixaEtaria[faixa][medicao]
}

function round(num: number, places = 3): number {
  return Math.round(num * Math.pow(10, places)) / Math.pow(10, places)
}
